import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { loadPdfJs } from '@/lib/pdfjs';

// iOS/iPadOS Safari paints only the first page of an <iframe> PDF as a static
// image (no scrolling, clipped to the page box), and Android Chrome doesn't
// render inline PDFs at all. On those devices we render pages ourselves with
// pdf.js; desktop keeps the native viewer (search, zoom, print).
function needsPdfJs(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports itself as a Mac; touch support gives it away
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isAndroid = /Android/i.test(ua);
  return isIOS || isIPadOS || isAndroid;
}

interface PdfViewerProps {
  url: string;
  title: string;
  onDownload?: (e: React.MouseEvent) => void;
}

export function PdfViewer({ url, title, onDownload }: PdfViewerProps) {
  const [usePdfJs] = useState(needsPdfJs);

  if (!usePdfJs) {
    return (
      <iframe
        src={url}
        className="w-full h-full border-0"
        title={title}
        onMouseDown={(e) => e.stopPropagation()}
      />
    );
  }

  return <PdfJsViewer url={url} onDownload={onDownload} />;
}

// ── pdf.js renderer ─────────────────────────────────────────────────────

// Cap the backing-store density: iOS kills tabs that hold too much canvas memory
const MAX_PIXEL_RATIO = 2;

function PdfJsViewer({ url, onDownload }: { url: string; onDownload?: (e: React.MouseEvent) => void }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageAspect, setPageAspect] = useState(11 / 8.5);
  const [error, setError] = useState(false);
  const [width, setWidth] = useState(0);
  const lastWidthRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    setDoc(null);
    setError(false);

    loadPdfJs()
      .then((pdfjs) => pdfjs.getDocument({ url }).promise)
      .then(async (pdf) => {
        loaded = pdf;
        if (cancelled) { pdf.destroy(); return; }
        const first = await pdf.getPage(1);
        const vp = first.getViewport({ scale: 1 });
        if (cancelled) return;
        setPageAspect(vp.height / vp.width);
        setDoc(pdf);
      })
      .catch((err) => {
        console.error('[PdfViewer] Failed to load PDF', err);
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
      loaded?.destroy();
    };
  }, [url]);

  // Track the container width only; page canvases change height, never width,
  // so ignoring height-only changes keeps this observer from looping.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const apply = (w: number) => {
      if (Math.abs(w - lastWidthRef.current) > 1) {
        lastWidthRef.current = w;
        setWidth(w);
      }
    };
    apply(el.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => apply(w), 100);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  const pageWidth = Math.max(0, width - 32);

  return (
    <div
      ref={scrollRef}
      className="absolute inset-0 overflow-auto overscroll-contain bg-muted/40"
      style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
      onMouseDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {error ? (
        <div className="h-full flex flex-col items-center justify-center gap-2 p-6 text-center">
          <p className="text-sm text-muted-foreground">PDF preview unavailable.</p>
          {onDownload && (
            <button onClick={onDownload} className="text-primary underline text-sm">Open PDF</button>
          )}
        </div>
      ) : !doc || pageWidth === 0 ? (
        <div className="h-full flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 py-4">
          {Array.from({ length: doc.numPages }, (_, i) => (
            <PdfPage
              key={i}
              doc={doc}
              pageNumber={i + 1}
              width={pageWidth}
              placeholderHeight={pageWidth * pageAspect}
              root={scrollRef.current}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PdfPageProps {
  doc: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  placeholderHeight: number;
  root: HTMLElement | null;
}

function PdfPage({ doc, pageNumber, width, placeholderHeight, root }: PdfPageProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isNear, setIsNear] = useState(pageNumber <= 2);
  const [renderedHeight, setRenderedHeight] = useState<number | null>(null);
  const height = renderedHeight ?? placeholderHeight;

  useEffect(() => {
    setRenderedHeight(null);
  }, [width]);

  // Only keep pages within a couple of screens of the viewport rendered
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsNear(entry.isIntersecting),
      { root, rootMargin: '150% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [root]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!isNear) {
      // Free the bitmap; the wrapper keeps its height so scroll position holds
      canvas.width = 0;
      canvas.height = 0;
      return;
    }

    let cancelled = false;
    let task: RenderTask | null = null;

    doc.getPage(pageNumber).then((page) => {
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const scale = width / base.width;
      const viewport = page.getViewport({ scale });
      const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);

      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      setRenderedHeight(viewport.height);

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      task = page.render({
        canvasContext: ctx,
        viewport,
        transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined,
      });
      task.promise.catch((err) => {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('[PdfViewer] Page render failed', err);
        }
      });
    });

    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [doc, pageNumber, width, isNear]);

  return (
    <div
      ref={wrapperRef}
      className="bg-white shadow-sm flex-shrink-0"
      style={{ width, height }}
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
  );
}
